import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useId } from 'react';

/**
 * @param {{
 *   children: string;
 *   as?: keyof JSX.IntrinsicElements;
 *   className?: string;
 *   style?: React.CSSProperties;
 *   variants?: import('motion/react').Variants;
 *   transition?: import('motion/react').Transition;
 * }} props
 */
export function TextMorph({
  children,
  as: Component = 'p',
  className,
  style,
  variants,
  transition,
}) {
  const uniqueId = useId();

  const characters = useMemo(() => {
    const charCounts = {};

    return children.split('').map((char) => {
      const lowerChar = char.toLowerCase();
      charCounts[lowerChar] = (charCounts[lowerChar] || 0) + 1;

      return {
        id: `${uniqueId}-${lowerChar}${charCounts[lowerChar]}`,
        label: char === ' ' ? '\u00A0' : char,
      };
    });
  }, [children, uniqueId]);

  const defaultVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const defaultTransition = {
    type: 'spring',
    stiffness: 280,
    damping: 18,
    mass: 0.3,
  };

  return (
    <Component className={cn(className)} aria-label={children} style={style}>
      <AnimatePresence mode="popLayout" initial={false}>
        {characters.map((character) => (
          <motion.span
            key={character.id}
            layoutId={character.id}
            className="inline-block"
            aria-hidden="true"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={variants || defaultVariants}
            transition={transition || defaultTransition}
          >
            {character.label}
          </motion.span>
        ))}
      </AnimatePresence>
    </Component>
  );
}
